import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const number = (value) => {
  const parsed = Number(String(value ?? "0").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};
const compact = (value) => new Intl.NumberFormat("en-IN", {
  notation: "compact",
  maximumFractionDigits: 2,
}).format(value);
const formatPercent = (value) => value == null || !Number.isFinite(value)
  ? "—"
  : `${value > 0 ? "+" : ""}${value.toFixed(1)}%`;
const changeClass = (value) => value > 0 ? "text-emerald-600" : value < 0 ? "text-rose-600" : "text-slate-400";

export default function VahanMonthly() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [dataset, setDataset] = useState("vehicle_class");
  const [periodMode, setPeriodMode] = useState("quarterly");
  const [yearDatasets, setYearDatasets] = useState([]);
  const [labelColumn, setLabelColumn] = useState("Vehicle Class");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [priorYearWarning, setPriorYearWarning] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setPriorYearWarning("");

    // Two prior calendar years are needed: the first displayed quarter is
    // Q1 of (year - 1), whose QoQ baseline is Q4 of (year - 2).
    Promise.allSettled([year - 2, year - 1, year].map(async (requestedYear) => {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/vahan/monthly/${requestedYear}/${dataset}`
      );
      return data;
    })).then((results) => {
      if (cancelled) return;
      const loaded = results
        .filter((result) => result.status === "fulfilled")
        .map((result) => result.value)
        .sort((a, b) => a.year - b.year);
      const selectedYearData = loaded.find((item) => item.year === year && item.rows?.length);

      if (!selectedYearData) {
        setYearDatasets([]);
        const failure = results[2]?.status === "rejected" ? results[2].reason : null;
        const isUnreachable = !failure?.response && Boolean(failure?.request);
        setError(failure?.response?.data?.error || (isUnreachable
          ? `Cannot reach the Macro Data API at ${process.env.REACT_APP_API_URL || "the configured API URL"}. Start tradestat-server.js in source/server, then reload this page.`
          : `Could not load monthly vehicle data for ${year}.`));
        return;
      }

      setYearDatasets(loaded);
      setLabelColumn(selectedYearData.labelColumn || (dataset === "maker" ? "Maker" : "Vehicle Class"));
      const missingYears = [year - 2, year - 1]
        .filter((requestedYear) => !loaded.some((item) => item.year === requestedYear && item.rows?.length));
      if (missingYears.length) {
        setPriorYearWarning(`Monthly CSV data was not returned for ${missingYears.join(", ")}. Comparisons that need those years will show —.`);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [year, dataset]);

  const currentDataset = yearDatasets.find((item) => item.year === year);
  const availableMonths = currentDataset?.months || [];
  const entities = useMemo(() => {
    const byName = new Map();
    yearDatasets.forEach((data) => data.rows.forEach((row) => {
      const name = String(row[labelColumn] || "").trim();
      if (!name) return;
      if (!byName.has(name)) byName.set(name, { name, monthlyValues: {} });
      data.months.forEach((month) => { byName.get(name).monthlyValues[month] = number(row[month]); });
    }));
    return [...byName.values()];
  }, [yearDatasets, labelColumn]);

  const periods = useMemo(() => {
    if (periodMode === "monthly") {
      return availableMonths.map((key) => ({ key, label: key, months: [key] }));
    }
    return [year - 1, year].flatMap((periodYear) => [1, 2, 3, 4].map((quarter) => {
      const months = MONTHS.slice((quarter - 1) * 3, quarter * 3)
        .map((month) => `${periodYear}-${month}`)
        .filter((month) => yearDatasets.some((data) => data.year === periodYear && data.months.includes(month)));
      return {
        key: `Q${quarter}-${String(periodYear).slice(-2)}`,
        label: `Q${quarter}-${String(periodYear).slice(-2)}${months.length > 0 && months.length < 3 ? "*" : ""}`,
        months,
      };
    }));
  }, [periodMode, availableMonths, yearDatasets, year]);

  const valueFor = (entity, period) => period.months.length
    ? period.months.reduce((sum, month) => sum + number(entity.monthlyValues[month]), 0)
    : null;
  const previousQuarterKey = (quarterKey) => {
    const match = quarterKey.match(/^Q([1-4])-(\d{2})$/);
    if (!match) return null;
    const quarter = Number(match[1]);
    const fullYear = Number(`20${match[2]}`);
    return quarter === 1
      ? `Q4-${String(fullYear - 1).slice(-2)}`
      : `Q${quarter - 1}-${String(fullYear).slice(-2)}`;
  };
  const monthsForQuarter = (quarterKey) => {
    const match = quarterKey.match(/^Q([1-4])-(\d{2})$/);
    if (!match) return [];
    const quarter = Number(match[1]);
    const fullYear = Number(`20${match[2]}`);
    const data = yearDatasets.find((item) => item.year === fullYear);
    if (!data) return [];
    return MONTHS.slice((quarter - 1) * 3, quarter * 3)
      .map((month) => `${fullYear}-${month}`)
      .filter((month) => data.months.includes(month));
  };
  const qoqFor = (entity, period) => {
    const currentMonths = monthsForQuarter(period.key);
    const previousMonths = monthsForQuarter(previousQuarterKey(period.key));
    // Q1 compares with the preceding year's Q4, whether or not Q4 is visible.
    // Require complete quarters so partial periods aren't compared unfairly.
    if (currentMonths.length !== 3 || previousMonths.length !== 3) return null;
    const current = currentMonths.reduce((sum, month) => sum + number(entity.monthlyValues[month]), 0);
    const previous = previousMonths.reduce((sum, month) => sum + number(entity.monthlyValues[month]), 0);
    return previous ? ((current - previous) / Math.abs(previous)) * 100 : null;
  };
  const momFor = (entity, monthKey) => {
    const [monthYear, monthName] = monthKey.split("-");
    const monthIndex = MONTHS.indexOf(monthName);
    if (monthIndex < 0) return null;
    const previousMonth = monthIndex === 0
      ? `${Number(monthYear) - 1}-Dec`
      : `${monthYear}-${MONTHS[monthIndex - 1]}`;
    if (!(previousMonth in entity.monthlyValues)) return null;
    const previous = number(entity.monthlyValues[previousMonth]);
    return previous ? ((number(entity.monthlyValues[monthKey]) - previous) / Math.abs(previous)) * 100 : null;
  };
  const yearOverYear = (entity) => {
    const thisYearMonths = yearDatasets.find((data) => data.year === year)?.months || [];
    const lastYearMonths = yearDatasets.find((data) => data.year === year - 1)?.months || [];
    if (!thisYearMonths.length || !lastYearMonths.length) return null;
    const thisYearTotal = thisYearMonths.reduce((sum, month) => sum + number(entity.monthlyValues[month]), 0);
    const lastYearTotal = lastYearMonths.reduce((sum, month) => sum + number(entity.monthlyValues[month]), 0);
    return lastYearTotal ? ((thisYearTotal - lastYearTotal) / Math.abs(lastYearTotal)) * 100 : null;
  };

  const visibleRows = useMemo(() => entities
    .map((entity) => ({
      ...entity,
      total: availableMonths.reduce((sum, month) => sum + number(entity.monthlyValues[month]), 0),
    }))
    .filter((entity) => entity.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => {
      const aTotal = a.name.toLowerCase() === "total";
      const bTotal = b.name.toLowerCase() === "total";
      return aTotal === bTotal ? b.total - a.total : aTotal ? -1 : 1;
    }), [entities, availableMonths, search]);

  const chartValues = periods.map((period) => {
    const total = entities.find((entity) => entity.name.toLowerCase() === "total");
    const value = total
      ? valueFor(total, period)
      : entities.reduce((sum, entity) => sum + number(valueFor(entity, period)), 0);
    return { ...period, value: period.months.length ? value : null };
  });
  const maxValue = Math.max(1, ...chartValues.map((item) => number(item.value)));
  const totalEntity = entities.find((entity) => entity.name.toLowerCase() === "total");
  const yearlyTotal = totalEntity
    ? availableMonths.reduce((sum, month) => sum + number(totalEntity.monthlyValues[month]), 0)
    : entities.reduce((sum, entity) => sum + availableMonths.reduce((rowSum, month) => rowSum + number(entity.monthlyValues[month]), 0), 0);
  const title = dataset === "maker" ? "Maker" : "Vehicle class";

  return (
    <section className="mx-auto max-w-7xl px-3 py-5 sm:px-5">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[.18em] text-blue-600">VAHAN · India</p>
          <h1 className="mt-1 text-2xl font-bold text-slate-900">Vehicle Registrations</h1>
          <p className="mt-1 text-sm font-medium text-slate-500">Monthly and quarterly registration counts from the generated CSV files.</p>
        </div>
        <label className="text-sm font-medium text-slate-600">Year
          <select value={year} onChange={(event) => setYear(Number(event.target.value))} className="ml-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900">
            {Array.from({ length: currentYear - 2021 + 1 }, (_, index) => currentYear - index).map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-xl bg-slate-100 p-1" role="tablist" aria-label="Vehicle registration dataset">
          {[["vehicle_class", "By vehicle class"], ["maker", "By maker"]].map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={dataset === key} onClick={() => setDataset(key)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${dataset === key ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-xl bg-slate-100 p-1" role="group" aria-label="Registration data period">
          {[["monthly", "Monthly"], ["quarterly", "Quarterly"]].map(([key, label]) => (
            <button key={key} type="button" aria-pressed={periodMode === key} onClick={() => setPeriodMode(key)}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${periodMode === key ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading ? <div className="rounded-2xl border bg-white p-10 text-center text-slate-500">Loading vehicle registration data…</div> : error ?
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{error}</div> : (
        <>
          {priorYearWarning && <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">{priorYearWarning} Restart the active `tradestat-server.js` API and confirm it can read the previous year’s CSV.</div>}
          <div className="mb-5 grid gap-3 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="text-sm text-slate-500">Total registrations · {year}</div>
              <div className="mt-2 text-3xl font-bold tracking-tight text-slate-900">{yearlyTotal ? compact(yearlyTotal) : "—"}</div>
              <div className="mt-1 text-xs text-slate-400">Sum of reported monthly counts</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-3 text-sm font-semibold text-slate-700">{periodMode === "quarterly" ? "Quarterly" : "Monthly"} trend · total registrations</div>
              <div className="flex h-24 items-end gap-2 overflow-x-auto">
                {chartValues.map(({ key, label, value }) => (
                  <div key={key} className="flex h-full min-w-10 flex-1 flex-col items-center justify-end gap-1" aria-label={`${label}: ${value == null ? "No data" : value.toLocaleString("en-IN")}`}>
                    <span className="text-[9px] text-slate-500">{value ? compact(value) : "—"}</span>
                    <div className="w-full max-w-10 rounded-t-md bg-blue-500" style={{ height: `${value ? Math.max(5, (value / maxValue) * 58) : 0}%` }} />
                    <span className="whitespace-nowrap text-[10px] font-medium text-slate-500">{periodMode === "monthly" ? label.slice(-3) : label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4">
              <div>
                <h2 className="font-semibold text-slate-900">{title} breakdown</h2>
                <p className="text-xs text-slate-500">{visibleRows.length} {dataset === "maker" ? "makers" : "vehicle classes"} · {periodMode === "quarterly" ? "last 8 quarters" : `${year} monthly values`}</p>
              </div>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`Search ${dataset === "maker" ? "makers" : "vehicle classes"}…`} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm sm:w-64" />
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-0 text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500"><tr>
                  <th className="sticky left-0 z-10 border-b border-r border-slate-200 bg-slate-50 px-4 py-3 text-left">{title}</th>
                  {periods.map((period) => <th key={period.key} className="whitespace-nowrap border-b border-r border-slate-200 px-4 py-3 text-right">{period.label}</th>)}
                  <th className="whitespace-nowrap border-b border-slate-200 px-4 py-3 text-right">YoY % change</th>
                </tr></thead>
                <tbody>
                  {visibleRows.map((row, index) => {
                    const yoy = yearOverYear(row);
                    return <tr key={`${row.name}-${index}`} className="hover:bg-slate-50">
                      <td className="sticky left-0 z-10 whitespace-nowrap border-b border-r border-slate-200 bg-white px-4 py-3 font-medium text-slate-800">{row.name}</td>
                      {periods.map((period) => {
                        const value = valueFor(row, period);
                        const change = periodMode === "quarterly" ? qoqFor(row, period) : momFor(row, period.key);
                        return <td key={period.key} className="whitespace-nowrap border-b border-r border-slate-200 px-4 py-2 text-right tabular-nums text-slate-600">
                          <div>{value == null || value === 0 ? "—" : value.toLocaleString("en-IN")}</div>
                          <div className={`text-[10px] font-medium ${changeClass(change)}`}>{formatPercent(change)}</div>
                        </td>;
                      })}
                      <td title={`Annual total ${year} compared with ${year - 1}`} className={`whitespace-nowrap border-b border-slate-200 px-4 py-3 text-right font-semibold tabular-nums ${changeClass(yoy)}`}>{formatPercent(yoy)}</td>
                    </tr>;
                  })}
                  {!visibleRows.length && <tr><td colSpan={periods.length + 2} className="px-4 py-10 text-center text-slate-500">No matching {dataset === "maker" ? "makers" : "vehicle classes"}.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
